/**
 * useSafeArea - 全局安全区高度管理
 *
 * 解决问题：App 首屏页面（jz.vue / purchasedModel.vue）与 App.vue onMounted 并发渲染，
 * appBridge.getStatusBarHeight() 是异步的，直接写 CSS 变量时 NavBar 已经用 0px 渲染完了。
 *
 * 方案：用 Vue 响应式 ref 存储高度，NavBar 通过 :style 绑定响应式值。
 * 当 App.vue 异步拿到真实高度后，ref 更新 → 所有依赖组件自动重渲染。
 *
 * 使用：
 *   import { safeAreaTop, setSafeAreaTop } from '@/composables/useSafeArea'
 *   // NavBar: :style="{ paddingTop: safeAreaTop + 'px' }"
 *   // App.vue: setSafeAreaTop(height)
 */
import { ref, computed } from 'vue'

// 单例：全局共享同一个 ref（模块级单例，不需要 Pinia）
const safeAreaTopPx = ref(0)

/**
 * 设置安全区高度（由 App.vue 在拿到真实高度后调用）
 */
export const setSafeAreaTop = (height: number) => {
    safeAreaTopPx.value = height
    // 同步写入 CSS 变量，供不走响应式绑定的遗留样式使用
    if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--safe-area-top', `${height}px`)
    }
}

/**
 * 读取状态栏高度（px 数值，响应式）
 */
export const safeAreaTop = safeAreaTopPx

/**
 * NavBar 使用的响应式 inline style
 * height = 0.44rem（导航内容区） + safeAreaTop
 * 注意：0.44rem 需要转换为 px，但 rem 值在 JS 里不易直接计算。
 * 因此改为：用 CSS min-height/padding-top 响应式控制，高度仍用 CSS，padding-top 用 JS 控制。
 */
export const navbarPaddingTopStyle = computed(() => ({
    paddingTop: `${safeAreaTopPx.value}px`,
}))

export default safeAreaTopPx
